import { Injectable, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Platform } from '@ionic/angular';
import type { DaylightPhase } from 'src/app/shared/services/appearance/appearance.service';

/**
 * Native-shell bootstrapping: status bar styling and the Android root back-button fallback.
 * `init()` is guarded by `Capacitor.isNativePlatform()`, so the service is a no-op in the
 * browser build.
 */
@Injectable({ providedIn: 'root' })
export class NativeService {
  private platform = inject(Platform);

  init(): void {
    if (!this.isNativePlatform()) {
      return;
    }

    if (this.getPlatform() === 'android') {
      // Edge-to-edge: draw behind the status bar; safe-area insets keep the header clear.
      // (iOS overlays the web view by default and rejects setOverlaysWebView.)
      this.setStatusBarOverlay();
      // Root fallback at the lowest priority: when nothing else handles hardware back,
      // minimize the app instead of exiting it.
      this.platform.backButton.subscribeWithPriority(-1, () => this.minimizeApp());
    }
  }

  syncStatusBar(phase: DaylightPhase): void {
    if (!this.isNativePlatform()) {
      return;
    }

    this.setStatusBarStyle(phase === 'night' ? Style.Light : Style.Dark);
  }

  // The Capacitor statics below are unspyable module-level proxies; each call is wrapped in a
  // small instance method so the spec can spyOn the seam (same pattern as the test suite uses).

  private isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  private getPlatform(): string {
    return Capacitor.getPlatform();
  }

  private setStatusBarStyle(style: Style): void {
    void StatusBar.setStyle({ style });
  }

  private setStatusBarOverlay(): void {
    void StatusBar.setOverlaysWebView({ overlay: true });
  }

  private minimizeApp(): void {
    void App.minimizeApp();
  }
}
